/**
 * Camera capture utilities for mobile and web
 */

export interface CameraConstraints {
  width?: { ideal: number; max: number };
  height?: { ideal: number; max: number };
  facingMode?: 'user' | 'environment';
}

export class CameraError extends Error {
  constructor(
    message: string,
    public code: 'NOT_SUPPORTED' | 'PERMISSION_DENIED' | 'DEVICE_NOT_FOUND' | 'UNKNOWN'
  ) {
    super(message);
    this.name = 'CameraError';
  }
}

/**
 * Check if camera API is supported
 */
export function isCameraSupported(): boolean {
  return !!(navigator.mediaDevices && window.MediaStream);
}

/**
 * Request camera access and return video stream
 */
export async function requestCameraStream(
  constraints: CameraConstraints = { facingMode: 'environment' }
): Promise<MediaStream> {
  if (!isCameraSupported()) {
    throw new CameraError(
      'Camera API is not supported on this device',
      'NOT_SUPPORTED'
    );
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: constraints.width || { ideal: 1280, max: 1920 },
        height: constraints.height || { ideal: 720, max: 1080 },
        facingMode: constraints.facingMode || 'environment',
      },
      audio: false,
    });
    return stream;
  } catch (error: any) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      throw new CameraError(
        'Camera permission denied. Please enable camera access in settings.',
        'PERMISSION_DENIED'
      );
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      throw new CameraError(
        'No camera device found on this device',
        'DEVICE_NOT_FOUND'
      );
    }
    throw new CameraError(
      `Camera error: ${error.message}`,
      'UNKNOWN'
    );
  }
}

/**
 * Stop camera stream
 */
export function stopCameraStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => {
    track.stop();
  });
}

/**
 * Capture a frame from a video element as an image
 */
export async function captureFrame(
  videoElement: HTMLVideoElement,
  options: { width?: number; height?: number; quality?: number } = {}
): Promise<File> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const width = options.width || videoElement.videoWidth || 1280;
  const height = options.height || videoElement.videoHeight || 720;
  const quality = options.quality ?? 0.9;

  if (width === 0 || height === 0) {
    throw new Error('Camera feed not ready yet');
  }

  canvas.width = width;
  canvas.height = height;

  ctx.drawImage(videoElement, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

  if (!blob) {
    throw new Error('Failed to capture frame');
  }

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  return new File([blob], `capture-${timestamp}.jpg`, { type: 'image/jpeg' });
}

/**
 * Convert canvas to File
 */
export function canvasToFile(
  canvas: HTMLCanvasElement,
  filename: string = 'capture.jpg',
  quality: number = 0.9
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to convert canvas to blob'));
          return;
        }
        const file = new File([blob], filename, { type: 'image/jpeg' });
        resolve(file);
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * List available cameras
 */
export async function listCameras(): Promise<MediaDeviceInfo[]> {
  if (!isCameraSupported()) {
    return [];
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput');
  } catch (error) {
    console.error('Error listing cameras:', error);
    return [];
  }
}

/**
 * Get back/environment camera
 */
export async function getBackCamera(): Promise<MediaDeviceInfo | null> {
  try {
    const cameras = await listCameras();
    // Try to find back camera
    const backCamera = cameras.find(
      (camera) =>
        camera.label.toLowerCase().includes('back') ||
        camera.label.toLowerCase().includes('rear') ||
        camera.label.toLowerCase().includes('environment')
    );
    return backCamera || cameras[0] || null;
  } catch (error) {
    console.error('Error getting back camera:', error);
    return null;
  }
}

/**
 * Request camera with specific device ID
 */
export async function requestCameraWithDevice(
  deviceId: string
): Promise<MediaStream> {
  if (!isCameraSupported()) {
    throw new CameraError(
      'Camera API is not supported on this device',
      'NOT_SUPPORTED'
    );
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
      },
      audio: false,
    });
    return stream;
  } catch (error: any) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      throw new CameraError(
        'Camera permission denied',
        'PERMISSION_DENIED'
      );
    }
    if (error.name === 'NotFoundError') {
      throw new CameraError(
        'Camera device not found',
        'DEVICE_NOT_FOUND'
      );
    }
    throw new CameraError(
      `Camera error: ${error.message}`,
      'UNKNOWN'
    );
  }
}

/**
 * Apply image filters
 */
export interface ImageFilterOptions {
  brightness?: number; // 0-200, 100 = normal
  contrast?: number; // 0-200, 100 = normal
  saturation?: number; // 0-200, 100 = normal
  blur?: number; // 0-50 pixels
  grayscale?: number; // 0-100%
}

export function applyImageFilter(
  canvas: HTMLCanvasElement,
  options: ImageFilterOptions
): string {
  const filters: string[] = [];

  if (options.brightness !== undefined) {
    filters.push(`brightness(${options.brightness}%)`);
  }
  if (options.contrast !== undefined) {
    filters.push(`contrast(${options.contrast}%)`);
  }
  if (options.saturation !== undefined) {
    filters.push(`saturate(${options.saturation}%)`);
  }
  if (options.blur !== undefined) {
    filters.push(`blur(${options.blur}px)`);
  }
  if (options.grayscale !== undefined) {
    filters.push(`grayscale(${options.grayscale}%)`);
  }

  return filters.join(' ');
}
